/**
 * LE BANDEAU — l'image pleine largeur de l'en-tête et celle du pied.
 *
 * Les deux se comportent exactement pareil, d'où un seul composant : même cadrage,
 * même ambilight, même réglages à deux clés près.
 *
 * ⚠️ IL MESURE, DONC IL EST EN JAVASCRIPT. Un `background-size: cover` ne sait pas ce
 * qu'est une zone à préserver : il rogne ce qui dépasse, sans regarder. Tenir la
 * promesse « cette zone reste entière » demande la largeur RÉELLE du bandeau, qui
 * dépend de l'écran, de l'ouverture du panneau et du format d'aperçu. D'où le
 * `ResizeObserver`, et le calcul dans `bandLayout.ts`.
 *
 * ⚠️ LES CÔTÉS VIDES SONT PEINTS AVEC LA COULEUR DU BORD DE L'IMAGE, pas avec un flou
 * d'elle. Voir `bandEdge.ts` : le flou a été essayé, et il bordait une affiche orange
 * de taches pâles prises à son centre.
 *
 * ⚠️ ET IL AFFICHE AVANT DE SAVOIR. Tant que l'image n'est pas chargée ou que la
 * largeur n'est pas connue, le bandeau garde le cadrage d'avant : un `cover` centré
 * sur le milieu de la zone. Sans ce repli, la première image de chaque visite
 * arriverait après un bandeau vide.
 */

import { useEffect, useState } from "react";
import { useSettings, useText } from "../settings/context";
import { fileUrl } from "../settings/fichier";
import { type Edges, readEdges, readableUrl } from "./bandEdge";
import {
  type BandFit,
  type Box,
  type SafeArea,
  bandHeights,
  bandRatio,
  fitBand,
  readSafe,
} from "./bandLayout";

/**
 * LE FONDU DES BORDS — ce qui empêche l'ambilight de se voir comme une arête.
 *
 * L'image nette s'arrête net et le flou reprend derrière : à l'œil, une couture
 * verticale. Quelques pixels d'effacement sur le bord la font disparaître.
 *
 * ⚠️ LES ARRÊTS SONT AUX BORDS DE L'IMAGE, PAS À CEUX DU BANDEAU. Première version
 * écrite en `0px` et `100%` : elle effaçait les bords de la boîte, c'est-à-dire
 * précisément le vide que le flou occupe, et ne touchait pas la couture. Rien ne
 * changeait à l'écran, et pour cause. La couche couvre tout le bandeau ; l'image,
 * elle, commence à `left` et finit `width` plus loin.
 *
 * ⚠️ ET SEULEMENT SUR LES BORDS QUE `fitBand` AUTORISE. Un fondu posé du côté où la
 * zone à préserver touche le bord de l'image effacerait ce qu'on avait promis
 * d'afficher entier — d'où une largeur calculée avec la géométrie, là-bas.
 *
 * `WebkitMaskImage` en plus du standard : les navigateurs des téléphones d'il y a
 * quelques années ne lisent que celui-là, et sans lui l'image revient simplement
 * sans fondu.
 */
function maskOf(fit: BandFit) {
  const { fade } = fit;
  if (fade.left < 0.5 && fade.right < 0.5) return {};
  const right = fit.left + fit.width;
  const mask = `linear-gradient(to right, transparent ${fit.left}px, #000 ${fit.left + fade.left}px, #000 ${right - fade.right}px, transparent ${right}px)`;
  return { maskImage: mask, WebkitMaskImage: mask };
}

export function Band({
  imageKey,
  focusKey,
  safeKey,
  sidesKey,
  heightKey,
  cfg,
  className,
  off,
}: {
  /** Le réglage qui porte l'image. */
  imageKey: string;
  /** Le réglage qui porte la hauteur choisie, s'il y en a une (voir `bandHeights`). */
  heightKey?: string;
  /**
   * L'ancien point focal, lu tant qu'aucune zone n'a été tracée. Absent d'un bandeau
   * né après lui — ceux de l'écran de salle : aucun événement n'a de point à y relire.
   */
  focusKey?: string;
  /** Le réglage qui porte la zone à préserver. */
  safeKey: string;
  /** Le réglage qui porte la couleur imposée aux côtés, s'il y en a une. */
  sidesKey: string;
  /** Le nom du bloc pour le panneau de réglages (surlignage et carte). */
  cfg: string;
  className?: string;
  /** Éteint par autre chose que l'absence d'image — le pied de page, par exemple. */
  off?: boolean;
}) {
  /* ⚠️ PASSÉ PAR `fileUrl` : une valeur reprise de la V1 est une CLÉ de fichier, pas
     une adresse, et posée telle quelle elle se résout contre le site. Voir
     `settings/fichier.ts`. */
  const image = fileUrl(useText(imageKey));
  const { value } = useSettings();
  const area = readSafe(useText(safeKey), focusKey ? value(focusKey) : undefined);
  /* Une couleur imposée aux côtés dispense d'aller la chercher sur l'image. */
  const sides = useText(sidesKey);
  const height = heightKey ? bandHeights(value(heightKey)) : null;
  return (
    <BandView
      image={image}
      area={area}
      sides={sides}
      height={height}
      cfg={cfg}
      className={className}
      off={off}
    />
  );
}

/**
 * LE BANDEAU SANS LES RÉGLAGES — ce qui mesure et ce qui dessine.
 *
 * Séparé de la lecture des réglages pour être montable seul : c'est ce qui permet de
 * le regarder dans un banc, avec le vrai CSS et de vraies largeurs, plutôt que de
 * juger un cadrage sur une capture d'écran de production.
 */
export function BandView({
  image,
  area,
  sides = "",
  height = null,
  cfg,
  className = "",
  off = false,
}: {
  image: string;
  area: SafeArea;
  /** La couleur imposée aux côtés ; vide, elle est prise sur le bord de l'image. */
  sides?: string;
  /** La hauteur choisie ; `null` laisse les défauts du CSS (120 px, 180 sur bureau). */
  height?: { mobile: number; desktop: number } | null;
  cfg?: string;
  className?: string;
  off?: boolean;
}) {
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const [box, setBox] = useState<Box | null>(null);
  const [natural, setNatural] = useState<Box | null>(null);
  const [edges, setEdges] = useState<Edges | null>(null);

  /**
   * ⚠️ LE NŒUD EN ÉTAT, PAS EN `ref`. L'identité d'une `ref` ne change jamais : rien
   * n'aurait prévenu l'effet qu'un bandeau était apparu. Même raison que pour le
   * nœud du site dans `Shell`.
   */
  useEffect(() => {
    if (!node) return;
    const watch = new ResizeObserver((entries) => {
      const measured = entries[0]?.contentRect;
      if (!measured) return;
      const { width, height } = measured;
      // Un état posé à chaque pixel ferait un rendu par pixel : on ne réécrit que
      // si la mesure a vraiment bougé.
      setBox((was) =>
        was && was.width === width && was.height === height
          ? was
          : { width, height },
      );
    });
    watch.observe(node);
    return () => watch.disconnect();
  }, [node]);

  /**
   * LA SONDE — la définition de l'image, et les couleurs de ses bords.
   *
   * Chargée à part : le bandeau est un FOND, il n'a pas d'`<img>` d'où lire
   * `naturalWidth`.
   *
   * ⚠️ DEUX CHARGEMENTS PLUTÔT QU'UN, ET DANS CET ORDRE. Lire les pixels exige
   * `crossOrigin="anonymous"`, mais une image servie SANS en-tête CORS ne se charge
   * alors pas du tout : les dimensions seraient perdues avec les couleurs, et le
   * cadrage entier tomberait en repli pour une question de décoration. On tente donc
   * la version lisible, et sur échec on recharge la même image sans rien demander,
   * pour ses seules dimensions. Le navigateur sert la seconde depuis son cache.
   *
   * ⚠️ LA VERSION LISIBLE A SA PROPRE ADRESSE (`readableUrl`, #548) : à la même que le
   * fond, Chrome lui resservait la réponse sans CORS et la lecture échouait toujours.
   */
  useEffect(() => {
    setNatural(null);
    setEdges(null);
    if (!image) return;
    let alive = true;

    const measure = (probe: HTMLImageElement) =>
      setNatural({ width: probe.naturalWidth, height: probe.naturalHeight });

    const plain = () => {
      const probe = new Image();
      probe.onload = () => {
        if (alive) measure(probe);
      };
      probe.src = image;
    };

    /* ⚠️ COULEUR IMPOSÉE = RIEN À LIRE. On saute la version « lisible » et son
       chargement à part : elle ne servirait qu'à deviner ce que l'organisateur a déjà
       décidé. */
    if (sides) {
      plain();
      return () => {
        alive = false;
      };
    }

    const readable = new Image();
    readable.crossOrigin = "anonymous";
    readable.onload = () => {
      if (!alive) return;
      measure(readable);
      setEdges(readEdges(readable));
    };
    readable.onerror = () => {
      if (alive) plain();
    };
    readable.src = readableUrl(image);

    return () => {
      alive = false;
    };
  }, [image, sides]);

  const fit = box && natural ? fitBand(box, natural, area) : null;
  /* La hauteur choisie n'est qu'un maximum : une image qui, cadrée, n'y monte pas
     ramène le bandeau à sa hauteur (voir `bandRatio`). */
  const ratio = bandRatio(natural, area);
  const url = `url(${JSON.stringify(image)})`;

  return (
    <div
      ref={setNode}
      className={className ? `band ${className}` : "band"}
      data-cfg={cfg}
      data-fit={ratio ? "image" : undefined}
      hidden={off || !image}
      /* La couleur imposée, ou celle qui tient le milieu entre les deux bords : c'est
         elle qui comble un vide en haut ou en bas, là où les deux bandes latérales ne
         vont pas. Sans rien de tout cela, la couleur de marque du CSS reste. */
      style={{
        ...(sides || edges ? { backgroundColor: sides || edges!.average } : {}),
        /* La hauteur choisie passe par deux variables que le CSS lit à chaque largeur :
           c'est lui qui sait où commence le bureau, pas ce composant. */
        ...(height
          ? ({ "--band-h": `${height.mobile}px`, "--band-h-desktop": `${height.desktop}px` } as React.CSSProperties)
          : {}),
        ...(ratio ? ({ "--band-ratio": String(ratio) } as React.CSSProperties) : {}),
      }}
    >
      {/* LES CÔTÉS — la couleur du bord de l'image, prolongée.
          Deux bandes, une par côté, exactement de la largeur du vide : la gauche
          prolonge le bord gauche, la droite le bord droit. Elles ne sont là que
          quand il y a du vide à couvrir et que les pixels ont pu être lus.
          Décoratif : rien à annoncer, rien à cliquer. */}
      {fit?.sides && (sides || edges) && box && (
        <>
          <div
            className="band__edge"
            style={{
              left: 0,
              /* ⚠️ ELLES PASSENT SOUS LE FONDU DE L'IMAGE, d'où la largeur du vide
                 PLUS celle du fondu. Arrêtées au bord de l'image, on voyait par
                 transparence le fond du bandeau : une barre sombre à l'endroit même
                 que le fondu devait faire disparaître. */
              width: `${Math.max(0, fit.left + fit.fade.left)}px`,
              backgroundColor: sides || edges!.left,
            }}
          />
          <div
            className="band__edge"
            style={{
              right: 0,
              width: `${Math.max(0, box.width - fit.left - fit.width + fit.fade.right)}px`,
              backgroundColor: sides || edges!.right,
            }}
          />
        </>
      )}
      <div
        className="band__pic"
        style={
          image
            ? fit
              ? {
                  backgroundImage: url,
                  backgroundSize: `${fit.width}px ${fit.height}px`,
                  backgroundPosition: `${fit.left}px ${fit.top}px`,
                  ...maskOf(fit),
                }
              : {
                  backgroundImage: url,
                  backgroundSize: "cover",
                  /* Le repli vise le MILIEU de la zone : son coin haut-gauche
                     cadrerait à côté, et le temps d'une mesure ça se voit. */
                  backgroundPosition: `${(area.x + area.w / 2) * 100}% ${(area.y + area.h / 2) * 100}%`,
                }
            : undefined
        }
      />
    </div>
  );
}
